import moment from 'moment';

/**
 * Format a date to the application's standard format
 * @param date Date object to format
 * @returns Formatted date string (DD/MM/YYYY HH:MM:SS)
 */
export const formatDateTime = (date: Date): string => {
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}/${date.getFullYear()} ${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes()
  ).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
};

/**
 * Check if a record is locked by a specific user
 * @param record Record to check
 * @param currentUser Current user identifier
 * @returns True if locked by current user
 */
export const isLockedByCurrentUser = (
  record: { lockedBy: string | null },
  currentUser: string
): boolean => {
  return record.lockedBy === currentUser;
};

/**
 * Check if a record is locked by another user
 * @param record Record to check
 * @param currentUser Current user identifier
 * @returns True if locked by another user
 */
export const isLockedByOtherUser = (
  record: { lockedBy: string | null },
  currentUser: string
): boolean => {
  return record.lockedBy !== null && record.lockedBy !== currentUser;
};

/**
 * Parse match percentage string to number
 * @param matchPercentage Match percentage string (e.g., "90%")
 * @returns Numeric value
 */
export const parseMatchPercentage = (matchPercentage: string | number): number => {
  return parseInt(String(matchPercentage).replace('%', '')) || 0;
};

/**
 * Get the current timestamp formatted
 * @returns Current timestamp in application format
 */
export const getCurrentTimestamp = (): string => {
  return formatDateTime(new Date());
};

/**
 * @param date Date object to format
 * @param format Format string with MM, DD, YYYY, HH, mm, SS tokens
 * @returns Formatted date string
 */
export const applyDateFormat = (date: Date, format: string): string => {
  const MM = String(date.getMonth() + 1).padStart(2, '0');
  const DD = String(date.getDate()).padStart(2, '0');
  const YYYY = String(date.getFullYear());
  const HH = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const SS = String(date.getSeconds()).padStart(2, '0');
  return format
    .replace('YYYY', YYYY)
    .replace('MM', MM)
    .replace('DD', DD)
    .replace('HH', HH)
    .replace('MM', mm)
    .replace('SS', SS);
};

/**
 * @param key Field key to check
 * @param value Raw string value from profile
 * @param dateFormat Format string (e.g. 'MM/DD/YYYY')
 * @param dateKeys Set of field keys that hold date values
 * @returns Formatted date string or original value
 */
export const formatFieldValue = (
  key: string,
  value: string | undefined,
  dateFormat: string,
  dateKeys: Set<string>
): string | undefined => {
  if (value && dateKeys.has(key)) {
    const m = moment(value, 'YYYY-MM-DD', true);
    if (m.isValid()) return m.format(dateFormat);
    const fallback = moment(value);
    if (fallback.isValid()) return fallback.format(dateFormat);
  }
  return value;
};

export const formatDateCell = (value: string | undefined, dateFormat: string): string => {
  if (!value) return '-';
  const m = moment(value, 'YYYY-MM-DD', true);
  if (m.isValid()) return m.format(dateFormat);
  const fallback = moment(value);
  return fallback.isValid() ? fallback.format(dateFormat) : '-';
};
